import { Draggable } from "@hello-pangea/dnd";
import { RecordContextProvider, useRedirect, useGetOne } from "ra-core";
import { Link } from "react-router";
import { User } from "lucide-react";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Job, Sale } from "../../providers/fakerest/dataGenerator/types";
import {
  getJobTypeLabel,
  getJobTypeBadgeClass,
  getJobTypeIcon,
} from "../constants";

export const JobCard = ({ job, index }: { job: Job; index: number }) => {
  if (!job) return null;

  return (
    <Draggable draggableId={String(job.id)} index={index}>
      {(provided, snapshot) => (
        <JobCardContent provided={provided} snapshot={snapshot} job={job} />
      )}
    </Draggable>
  );
};

export const JobCardContent = ({
  provided,
  snapshot,
  job,
}: {
  provided?: any;
  snapshot?: any;
  job: Job;
}) => {
  const redirect = useRedirect();
  const { data: assignedUser } = useGetOne<Sale>("sales", {
    id: job.assigned_to,
  });

  const formattedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(job.estimated_value);

  // Truncate address to city only for card display
  const shortAddress = job.address.split(",").slice(0, 2).join(",");

  const handleClick = (e: React.MouseEvent) => {
    // Only navigate if not dragging
    if (!snapshot?.isDragging) {
      redirect(`/jobs/${job.id}/show`);
    }
  };

  return (
    <div
      className="cursor-grab active:cursor-grabbing"
      {...provided?.draggableProps}
      {...provided?.dragHandleProps}
      ref={provided?.innerRef}
      onClick={handleClick}
    >
      <RecordContextProvider value={job}>
        <Card
          className={`bg-white dark:bg-gray-800 transition-all duration-200 cursor-pointer py-0 gap-0 ${
            snapshot?.isDragging
              ? "opacity-90 rotate-1 shadow-lg"
              : "shadow-sm hover:shadow-md"
          }`}
        >
          <CardContent className="p-3 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm flex-1 min-w-0">
                <ReferenceField
                  source="contact_id"
                  reference="contacts"
                  link={false}
                >
                  <TextField source="name" />
                </ReferenceField>
              </div>
              {/* Assigned user avatar - clickable */}
              {assignedUser ? (
                <Link
                  to={`/sales/${assignedUser.id}/show`}
                  onClick={(e) => e.stopPropagation()}
                  title={`${assignedUser.first_name} ${assignedUser.last_name}`}
                  className="shrink-0 ml-2 hover:ring-2 hover:ring-primary/50 rounded-full transition-all"
                >
                  {assignedUser.avatar?.src ? (
                    <img
                      src={assignedUser.avatar.src}
                      alt={`${assignedUser.first_name} ${assignedUser.last_name}`}
                      className="w-6 h-6 rounded-full"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                      <User className="h-3 w-3 text-muted-foreground" />
                    </div>
                  )}
                </Link>
              ) : (
                <div
                  className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 ml-2"
                  title="Unassigned"
                >
                  <User className="h-3 w-3 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {shortAddress}
            </div>
            <div className="flex flex-col gap-1 items-start">
              {(() => {
                const JobTypeIcon = getJobTypeIcon(job.job_type);
                return (
                  <Badge
                    variant="secondary"
                    className={`text-xs ${getJobTypeBadgeClass(job.job_type)} flex items-center gap-1`}
                  >
                    <JobTypeIcon className="h-3 w-3" />
                    {getJobTypeLabel(job.job_type)}
                  </Badge>
                );
              })()}
              <span className="text-sm font-semibold">{formattedValue}</span>
            </div>
          </CardContent>
        </Card>
      </RecordContextProvider>
    </div>
  );
};
